import {
  BaseRecord,
  useCreate,
  useDelete,
  useGetIdentity,
  useInvalidate,
  useUpdate,
} from '@refinedev/core'
import { useForm, Controller } from 'react-hook-form'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import { useState } from 'react'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { enGB } from 'date-fns/locale'
import { User } from '@/utils/auth/auth0'
import {
  validateCloseDate,
  validateOpenDate,
} from '@/utils/fundingRoundValidation'
import { UUID } from 'crypto'

interface FundingRoundManagementProps {
  /**
   * "live" (default): create/update/delete fire API calls against
   * `/admin/funding-rounds/`. Caller passes the existing rounds via
   * `fundingRoundsData` and supplies `opportunityUuid`.
   *
   * "draft": no API calls. Caller supplies `rounds` + `onRoundsChange`
   * and the component mutates the list in-memory through the callback.
   * Used on the create page where the opp doesn't exist yet — the rounds
   * travel with the OpportunityCreate request body.
   */
  mode?: 'live' | 'draft'
  // Live-mode props
  opportunityUuid?: UUID
  fundingRoundsData?: BaseRecord[] | undefined
  // Draft-mode props
  rounds?: DraftFundingRound[]
  onRoundsChange?: (rounds: DraftFundingRound[]) => void
}

interface FundingRound {
  id: number
  opportunity_uuid: UUID
  application_open_date: string | null
  application_close_date: string | null
  decision_date: string | null
  estimate_date_flag: boolean
  /** Concurrency token — see the update call in `handleCreateOrUpdate`. */
  revision_version?: number
}

/** Shape of a draft-mode round — no DB id, dates as ISO strings. */
export interface DraftFundingRound {
  application_open_date: string | null
  application_close_date: string | null
  decision_date: string | null
  estimate_date_flag: boolean
}

interface FundingRoundFormData {
  application_open_date: Date | null
  application_close_date: Date | null
  decision_date: Date | null
  estimate_date_flag: boolean
}

const formatDateForAPI = (date: Date | null): string | null => {
  if (!date) return null
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .split('T')[0]
}

export default function FundingRoundManagement({
  mode = 'live',
  opportunityUuid,
  fundingRoundsData,
  rounds: draftRounds,
  onRoundsChange,
}: FundingRoundManagementProps) {
  const isDraft = mode === 'draft'

  // Effective rows the table renders. In live mode this is whatever the
  // caller passed; in draft mode we synthesise BaseRecord-shaped rows so
  // the same table cells / edit dialog work unchanged. The synthetic `id`
  // is the array index so handleOpenDialog / handleDelete have a stable
  // handle to the row.
  const tableRows: BaseRecord[] | undefined = isDraft
    ? (draftRounds ?? []).map((r, idx) => ({ ...r, id: idx }))
    : fundingRoundsData
  const [editingRound, setEditingRound] = useState<FundingRound | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const {
    handleSubmit,
    control,
    reset,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<FundingRoundFormData>()

  const { data: user } = useGetIdentity() as User

  const { mutate: createFundingRound } = useCreate({
    meta: {
      updated_by:
        user?.name || `${user?.given_name || ''} ${user?.family_name || ''}`,
    },
    errorNotification: (error) => {
      return {
        message: 'Error creating funding round',
        description:
          error?.message ||
          'An error occurred while creating the funding round',
        type: 'error',
      }
    },
  })
  const { mutate: updateFundingRound } = useUpdate({
    meta: {
      updated_by:
        user?.name || `${user?.given_name || ''} ${user?.family_name || ''}`,
    },
    errorNotification: (error) => {
      return {
        message: 'Error updating funding round',
        description:
          error?.message ||
          'An error occurred while updating the funding round',
        type: 'error',
      }
    },
  })
  const { mutate: deleteFundingRound } = useDelete()
  const invalidate = useInvalidate()

  const handleCreateOrUpdate = (data: FundingRoundFormData) => {
    if (isDraft) {
      const newRound: DraftFundingRound = {
        application_open_date: formatDateForAPI(data.application_open_date),
        application_close_date: formatDateForAPI(data.application_close_date),
        decision_date: formatDateForAPI(data.decision_date),
        estimate_date_flag: data.estimate_date_flag,
      }
      const current = draftRounds ?? []
      const next =
        editingRound != null
          ? current.map((r, idx) => (idx === editingRound.id ? newRound : r))
          : [...current, newRound]
      onRoundsChange?.(next)
      handleCloseDialog()
      return
    }
    if (editingRound) {
      // Update existing round
      updateFundingRound(
        {
          resource: 'funding-rounds',
          id: editingRound.id,
          values: {
            funding_round: {
              application_open_date: data.application_open_date
                ? formatDateForAPI(data.application_open_date)
                : null,
              application_close_date: data.application_close_date
                ? formatDateForAPI(data.application_close_date)
                : null,
              decision_date: data.decision_date
                ? formatDateForAPI(data.decision_date)
                : null,
              estimate_date_flag: data.estimate_date_flag,
            },
            // GH#1969 case 2: this dialog holds a full snapshot taken when it
            // was opened. Sending the token it read lets the API reject the
            // save if a review-queue approval landed on this round meanwhile,
            // instead of silently restoring the pre-approval dates.
            revision_version: editingRound.revision_version,
            updated_by:
              user?.name ||
              `${user?.given_name || ''} ${user?.family_name || ''}`,
          },
          errorNotification: (error) => {
            // House convention — refine surfaces the status either way; see
            // `pages/opportunities/edit.tsx`. Checking only `statusCode` meant
            // this branch never fired and the reviewer got the generic error.
            if ((error?.statusCode ?? error?.response?.status) === 409) {
              return {
                message: 'This funding round changed while you were editing',
                description:
                  'A review-queue approval updated it after you opened this ' +
                  'dialog. Close and reopen the round to see the current ' +
                  'dates, then reapply your change.',
                type: 'error',
              }
            }
            return {
              message: 'Error updating funding round',
              description:
                error?.message ||
                'An error occurred while updating the funding round',
              type: 'error',
            }
          },
        },
        {
          onSuccess: () => {
            invalidate({
              resource: 'funding-rounds',
              invalidates: ['list'],
            })
            handleCloseDialog()
          },
        },
      )
    } else {
      // Create new round
      createFundingRound(
        {
          resource: 'funding-rounds',
          values: {
            opportunity_uuid: opportunityUuid,
            funding_round: {
              application_open_date: formatDateForAPI(
                data.application_open_date,
              ),
              application_close_date: formatDateForAPI(
                data.application_close_date,
              ),
              decision_date: formatDateForAPI(data.decision_date),
              estimate_date_flag: data.estimate_date_flag,
            },
          },
          errorNotification: (error) => {
            return {
              message: 'Error creating funding round',
              description:
                error?.message ||
                'An error occurred while creating the funding round',
              type: 'error',
            }
          },
        },
        {
          onSuccess: () => {
            invalidate({
              resource: 'funding-rounds',
              invalidates: ['list'],
            })
            handleCloseDialog()
          },
        },
      )
    }
  }

  const handleDelete = (id: number) => {
    if (!window.confirm('Are you sure you want to delete this funding round?'))
      return
    if (isDraft) {
      const next = (draftRounds ?? []).filter((_, idx) => idx !== id)
      onRoundsChange?.(next)
      return
    }
    deleteFundingRound(
      {
        resource: 'funding-rounds',
        id,
        meta: {
          updated_by:
            user?.name ||
            `${user?.given_name || ''} ${user?.family_name || ''}`,
        },
        errorNotification: (error) => {
          return {
            message: 'Error deleting funding round',
            description:
              error?.message ||
              'An error occurred while deleting the funding round',
            type: 'error',
          }
        },
      },
      {
        onSuccess: () => {
          invalidate({
            resource: 'funding-rounds',
            invalidates: ['list'],
          })
        },
      },
    )
  }

  const handleOpenDialog = (round?: FundingRound) => {
    if (round) {
      setEditingRound(round)
      reset({
        application_open_date: round.application_open_date
          ? new Date(round.application_open_date)
          : null,
        application_close_date: round.application_close_date
          ? new Date(round.application_close_date)
          : null,
        decision_date: round.decision_date
          ? new Date(round.decision_date)
          : null,
        estimate_date_flag: round.estimate_date_flag,
      })
    } else {
      setEditingRound(null)
      reset({
        application_open_date: null,
        application_close_date: null,
        decision_date: null,
        estimate_date_flag: false,
      })
    }
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setEditingRound(null)
    reset()
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Funding Rounds</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenDialog()}
          >
            Add Funding Round
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Application Open</TableCell>
                <TableCell>Application Close</TableCell>
                <TableCell>Decision Date</TableCell>
                <TableCell>Dates are estimated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!tableRows && !isDraft ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Loading funding rounds...
                  </TableCell>
                </TableRow>
              ) : tableRows && tableRows.length > 0 ? (
                tableRows.map((round) => (
                  <TableRow key={round.id}>
                    <TableCell>
                      {round.application_open_date
                        ? new Date(
                            round.application_open_date,
                          ).toLocaleDateString('en-GB')
                        : 'Not set'}
                    </TableCell>
                    <TableCell>
                      {round.application_close_date
                        ? new Date(
                            round.application_close_date,
                          ).toLocaleDateString('en-GB')
                        : 'Not set'}
                    </TableCell>
                    <TableCell>
                      {round.decision_date
                        ? new Date(round.decision_date).toLocaleDateString(
                            'en-GB',
                          )
                        : 'Not set'}
                    </TableCell>
                    <TableCell>
                      {round.estimate_date_flag ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        onClick={() => handleOpenDialog(round as FundingRound)}
                        sx={{ mr: 1 }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleDelete(round.id as number)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No funding rounds found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingRound ? 'Edit Funding Round' : 'Add Funding Round'}
          </DialogTitle>
          <form onSubmit={handleSubmit(handleCreateOrUpdate)}>
            <DialogContent>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Controller
                  name="application_open_date"
                  control={control}
                  rules={{
                    validate: (value) =>
                      validateOpenDate(
                        value,
                        getValues('application_close_date'),
                      ),
                  }}
                  render={({ field }) => (
                    <DatePicker
                      label="Application Open Date"
                      value={field.value}
                      onChange={(date: Date | null) => {
                        field.onChange(date)
                        trigger('application_close_date')
                      }}
                      format="dd/MM/yyyy"
                      slotProps={{
                        field: { clearable: true },
                        textField: {
                          error: !!errors.application_open_date,
                          helperText: errors.application_open_date?.message,
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Controller
                  name="application_close_date"
                  control={control}
                  rules={{
                    validate: (value) =>
                      validateCloseDate(
                        value,
                        getValues('application_open_date'),
                      ),
                  }}
                  render={({ field }) => (
                    <DatePicker
                      label="Application Close Date"
                      value={field.value}
                      onChange={(date: Date | null) => {
                        field.onChange(date)
                        trigger('application_open_date')
                      }}
                      format="dd/MM/yyyy"
                      slotProps={{
                        field: { clearable: true },
                        textField: {
                          error: !!errors.application_close_date,
                          helperText: errors.application_close_date?.message,
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Controller
                  name="decision_date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Decision Date"
                      value={field.value}
                      onChange={(date: Date | null) => field.onChange(date)}
                      format="dd/MM/yyyy"
                      slotProps={{ field: { clearable: true } }}
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Controller
                  name="estimate_date_flag"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                        />
                      }
                      label="Dates are estimated"
                    />
                  )}
                />
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained" color="primary">
                {editingRound ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
    </LocalizationProvider>
  )
}
